import { useChatStore } from "@/modules/assistant/states/index.ts";
import { useLoggedInMember } from "@/modules/auth/states";
import { updateAgentSessionStep, reopenAgentSession } from "@/modules/sessions/requests";
import {
  AgentSessionStatus,
  AgentSessionStepType,
  AgentSessionType,
} from "@/modules/sessions/types";
import { getZaniaTeamMemberRoles } from "@/modules/team/states/index.ts";
import { ZButton } from "@/shared/components/button";
import { ZDropdownButton } from "@/shared/components/button/DropdownButton.tsx";
import { LoadingSpinner } from "@/shared/components/custom/spin/index.tsx";
import ViewSessionsFilesDrawer from "@/shared/components/ViewSessionsFilesDrawer/index.tsx";
import useDrawerStore from "@/shared/components/ViewSessionsFilesDrawer/useDrawerStore.ts";
import { ZTooltip } from "@/shared/components/ZTooltip/ZTooltip.tsx"; // Added ZTooltip import
import { addNotification } from "@/shared/states/notification";
import parsePromise from "@/shared/utils/parsePromise.ts";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger
} from "@nextui-org/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  ArrowLeft,
  CheckCheck,
  CheckCircleIcon,
  ChevronDown,
  Ellipsis,
  FileStack,
  RotateCcw,
  Sparkles,
  Users
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { processDataBasedOnAssessment } from "../../components/upload-dialog/util";
import { helper } from "../../constants/helpers";
import {
  getAgentStateActions,
  getAgentTypeFromId,
  useAgentData,
  useAgentMainData,
  useAgentStepData,
  useApprovedIds,
  useReassessmentModeStore,
  useReviewResponse,
} from "../../states";
import {
  getCustomizeControlsActions,
  isSoc2ControlValid,
  isVendorAssessmentControlValid,
  NistCsfControl,
  Soc2Control,
  TSCABBRIVATIONS,
  useCustomizeControlsStore,
  VendorAssessmentControl
} from "../../states/cusomize-controls.ts";
import { isReadyToGenerateResponse } from "../../states/questionnaire";
import { AGENT_TYPES, AgentSourceFile, GapAssessmentTypes, RiskAssessmentTypes } from "../../types/index.ts";
import { handleAutoSaveCustomizeControls, handleGenerateResponse } from "../../use-cases/customize-control.use-case";
import { generateResponseData } from "../../use-cases/quesitionnaire.use-case";
import { generateAndDownloadSessionReport } from "../../utils/downloadReport.ts";
import { handleEndSession } from "../../utils/handle-end-session";
import { isInValidIdForSoc2 } from "../customize-controls/helpers.tsx";
import NistCsfReassessModal from "../customize-controls/NistCsfReassessModal";
import ReassessModal from "../review-responses/ReassessModal.tsx";
import { useVendorRiskAssessmentStore } from "../VendorRiskAssessment/useVendorRiskAssessment/useVendorRiskAssessmentStore.ts";
import EndSessionModal from "./EndSessionModal";
import ReopenSessionModal from "./ReopenSessionModal";
import ShareSessionModal from "./ShareSessionModal";
import WarningModal from "./WarningModal";
import { ROUTES } from "@/shared/constants/routes.ts";
import { getBackNavigation } from "../../utils/navigation.ts";

interface QAGenerateButtonProps {
  onGenerateResponse: () => Promise<void>;
  loading: boolean;
}

const QAGenerateButton = ({
  onGenerateResponse,
  loading,
}: QAGenerateButtonProps) => {
  const { id } = useParams<keyof IdParams>() as IdParams;
  const { agentType } = getAgentTypeFromId(id);
  const agentMainData = useAgentMainData(id);

  const allowGenerate = useMemo(
    () =>
      agentMainData &&
      agentType === AGENT_TYPES.QUESTIONNAIRE &&
      isReadyToGenerateResponse(id),
    [id, agentType, agentMainData]
  );

  return (
    <div className="inline-flex items-center">
      <ZButton
        onClick={() => void onGenerateResponse()}
        isLoading={loading}
        className="px-3 py-1 h-auto"
        isDisabled={!allowGenerate}
      >
        Generate Responses
      </ZButton>
    </div>
  );
};

export interface IdParams {
  id: string;
}

const AgentHeader = () => {
  const navigate = useNavigate();
  const { id } = useParams<keyof IdParams>() as IdParams;
  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get("mode") === "view";
  const { agentType, agentSubType } = getAgentTypeFromId(id);
  const helperData = helper[agentSubType];
  const [downloadReportLoading, setDownloadReportLoading] = useState(false);
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);
  const [isShareSessionModalOpen, setIsShareSessionModalOpen] = useState(false);
  const [isEndSessionModalOpen, setIsEndSessionModalOpen] = useState(false);
  const [isReopenSessionModalOpen, setIsReopenSessionModalOpen] = useState(false);
  const [endSessionLoading, setEndSessionLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openReassess, setOpenReassess] = useState<boolean>(false);
  const [uniqueFiles, setUniqueFiles] = useState<AgentSourceFile[]>([]);
  const [showReassessControl, setShowReassessControl] = useState<boolean>(
    false
  );
  const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false);
  const [missingControlsCount, setMissingControlsCount] = useState<number>(0);
  const [totalControlsCount, setTotalControlsCount] = useState<number>(0);
  const [isNistCsfReassessModalOpen, setIsNistCsfReassessModalOpen] = useState<boolean>(false);
  const [selectedReassessOption, setSelectedReassessOption] = useState<string>("REASSESS_ALL");
  const selectedOptionRef = useRef<string>("REASSESS_ALL");


  const { ids } = useReviewResponse(id);
  const stepData = useAgentStepData(id);
  const agentMainData = useAgentMainData(id);
  const agentData = useAgentData(id);
  const approved = useApprovedIds(id);
  const { isSaving } = useVendorRiskAssessmentStore();
  const { setAgentData } = getAgentStateActions();

  const openViewFilesUsedDrawer = () => {
  useDrawerStore.getState().openDrawer();
};
  
  /*
  enableSoc2Reassess FF will have value in the below format
  {
    "{orgId}": true/false
  }
  */
  const { enableSoc2Reassess, enablePylonChat } = useFlags();
  const loggedInMember = useLoggedInMember();
  const { canAccessZaniaAskAi } = loggedInMember ? getZaniaTeamMemberRoles("", loggedInMember) : { canAccessZaniaAskAi: false };

  // customize control checking validation
  const {
    dataState,
    isSavingControls,
    agentType: agentTypeFromCustomizeControls,
    isEvidenceMapping: ifSessionHasEvidenceMapping
  } = useCustomizeControlsStore();

  // ask ai
  const {setIsFloatingAskAiOpen} = useChatStore();

  // Get reassessment mode state from store
  const { isReassessmentMode, setReassessmentMode, agentId: reassessmentAgentId } = useReassessmentModeStore();

  // Memoize back navigation to avoid recomputing on every render
  const backNav = useMemo(
    () => getBackNavigation(agentType, agentSubType),
    [agentType, agentSubType]
  );

  const enableReassess = [
    AGENT_TYPES.RISK_ASSESSMENT,
    AGENT_TYPES.GAP_ASSESSMENT,
    AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
  ];
  
  // Calculate NIST control file status
  const { nistControls, totalNistControls, nistControlsMissingFiles } = useMemo(() => {
    if (dataState && Array.isArray(dataState) && agentSubType === RiskAssessmentTypes.NIST_CSF_2) {
      const controls = dataState.filter(control =>
        'agentType' in control &&
        control.agentType === AgentSessionType.RISK_ASSESSMENT
      ) as NistCsfControl[];
      
      const total = controls.length;
      
      const missing = controls.filter(control => {
        const hasNoFiles =
          (!control.source_urls || control.source_urls.length === 0) &&
          (!control.new_uploaded_files || control.new_uploaded_files.length === 0);
        return hasNoFiles;
      }).length;
      
      return { nistControls: controls, totalNistControls: total, nistControlsMissingFiles: missing };
    }
    return { nistControls: [], totalNistControls: 0, nistControlsMissingFiles: 0 };
  }, [dataState]);

  const { controlsCount, controlsWithMissingFiles } = useMemo(() => {
    if (dataState && Array.isArray(dataState) && agentType === AGENT_TYPES.GAP_ASSESSMENT) {
      const controls = dataState as Soc2Control[];
      
      const total = controls.length;
      
      const missing = controls.filter(control => {
        const hasNoFiles =
          (!control.source_urls || control.source_urls.length === 0) &&
          (!control.new_uploaded_files || control.new_uploaded_files.length === 0);
        return hasNoFiles;
      }).length;
      
      return { controlsCount: total, controlsWithMissingFiles: missing };
    }
    return { controlsCount: 0, controlsWithMissingFiles: 0 };
  }, [dataState]);


  const onClose = () => {
    setOpenReassess(false);
  };

  const handleEndSessionWithLoading = async () => {
    setEndSessionLoading(true);
    try {
      await handleEndSession(id, agentType, navigate);
    } finally {
      setEndSessionLoading(false);
      setIsEndSessionModalOpen(false);
    }
  };

  const handleReopenSession = async () => {
    // Prevent double-clicking
    if (endSessionLoading) return;

    setEndSessionLoading(true);
    try {
      // Call API to reopen session
      const response = await reopenAgentSession(id);

      // Validate response data
      if (!response?.data?.steps || !Array.isArray(response.data.steps)) {
        throw new Error("Invalid response data from server");
      }

      // Verify prepare_report step exists
      const prepareReportStep = response.data.steps.find(
        (step: any) => step.type === AgentSessionStepType.PREPARE_REPORT
      );

      if (!prepareReportStep) {
        throw new Error("Prepare report step not found in response");
      }

      // Update Zustand store with new session data
      const { updateAgentStepData, updateAgentSessionData } = getAgentStateActions();
      updateAgentStepData(id, response.data.steps);

      // Update session data to include reopened_at and reopened_by
      if (response.data.session) {
        updateAgentSessionData(id, response.data.session);
      }

      // Close modal
      setIsReopenSessionModalOpen(false);

     
    } catch (error: any) {
      console.error("Error reopening session:", error);

      // Generic error message for all failures
      addNotification({
        type: "error",
        title: "Error",
        message: "Something went wrong. Please try again.",
      });

      // Keep modal open so user can retry
    } finally {
      setEndSessionLoading(false);
    }
  };

  const getPreviousFiles = () => {
    const fileNames: string[] = [];
    const uniqueSources: AgentSourceFile[] = [];
    for (const sourceFileMap of agentMainData?.sourceFilesUrls || []) {
      for (const item of sourceFileMap) {
        const file: AgentSourceFile = item as AgentSourceFile;
        if (file.url) {
          if (!fileNames.includes(file.fileName || "")) {
            fileNames.push(file.fileName || "");
            uniqueSources.push(file);
          }
        }
      }
    }
    setUniqueFiles(uniqueSources);
  };

  useEffect(() => {
    if (agentData) {
      if (enableReassess.includes(agentData.agentType)) {
         setShowReassessControl(true);
      } else {
        setShowReassessControl(false);
      }
    } else {
      setShowReassessControl(false);
    }
  }, [agentData, enableSoc2Reassess, loggedInMember?.organization_id]);

  const handleReassess = () => {
    // For NIST CSF 2.0, go directly to the evidence mapping page
    const isEvidenceMapping = agentData?.sessionData && 
      'risk_assessment' in agentData.sessionData ? 
      agentData.sessionData.risk_assessment?.evidence_mapping : 
      false;

    const isSoc2EvidenceMapping = agentType === AGENT_TYPES.GAP_ASSESSMENT && ifSessionHasEvidenceMapping

    if (agentType === AGENT_TYPES.RISK_ASSESSMENT && agentSubType === RiskAssessmentTypes.NIST_CSF_2 && isEvidenceMapping) {
      // Find and update the evidence mapping step status
      if (agentData?.stepData) {
        
        const updatedStepData = [...agentData.stepData];
        
        // Find the evidence mapping step and set it to INPUT_NEEDED
        for (let i = 0; i < updatedStepData.length; i++) {
          if (updatedStepData[i].type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING) {
            updatedStepData[i].status = AgentSessionStatus.INPUT_NEEDED;
          }
          if (updatedStepData[i].type === AgentSessionStepType.GENERATE_RISK_ASSESSMENT_RESPONSE) {
            updatedStepData[i].status = AgentSessionStatus.PENDING;
          }
        }
        
        setAgentData(id, {
          ...agentData,
          stepData: updatedStepData
        });
      }
      
      // Enter reassessment mode
      setReassessmentMode(true, id);
      
      // Navigate to the main agent page - the component will recognize the evidence mapping step
      navigate(`/agent/${id}`);
      return; // Return early as we don't need to open the generic ReassessModal
    }

    if(isSoc2EvidenceMapping && agentData){
       const updatedStepData = [...agentData.stepData];

       for (let i = 0; i < updatedStepData.length; i++) {
          if (updatedStepData[i].type === AgentSessionStepType.CONTROL_SELECTION) {
            updatedStepData[i].status = AgentSessionStatus.INPUT_NEEDED;
          }
          if (updatedStepData[i].type === AgentSessionStepType.EDIT_RESPONSE) {
            updatedStepData[i].status = AgentSessionStatus.PENDING;
          }
        }

        setAgentData(id, {
          ...agentData,
          stepData: updatedStepData
        });
        
      
      // Enter reassessment mode
      setReassessmentMode(true, id);
      
      // Navigate to the main agent page - the component will recognize the evidence mapping step
      navigate(`/agent/${id}`);
      return; // Return early as we don't need to open the generic ReassessModal

    }
    
    // For other assessment types, continue with original flow
    getPreviousFiles();
    setOpenReassess(true);
  };

  // Function to handle start assessment click
  const handleStartAssessmentClick = () => { 
    // For NIST CSF 2.0 & GAP assessment, check if documents are missing and show warning modal
    if ((agentType === AGENT_TYPES.RISK_ASSESSMENT && agentSubType === RiskAssessmentTypes.NIST_CSF_2) || agentType === AGENT_TYPES.GAP_ASSESSMENT) {
      // Count controls missing documents
      let missingCount = 0;
      let totalCount = 0;
      
      if (dataState && Array.isArray(dataState)) {
        // Filter to only include NIST controls
        const nistControls = dataState.filter(control => 
          control.agentType === AgentSessionType.RISK_ASSESSMENT ? 'agentType' in control : (control as Soc2Control).tsc_id
        ) as NistCsfControl[] | Soc2Control[];
        
        totalCount = nistControls.length;
        
        // Count controls without documents
        missingCount = nistControls.filter(control => {
          // For NIST CSF controls, check file_names property
          return !control.file_names || control.file_names.length === 0;
        }).length;
        
      } else {
        // Fallback values if data is not available
        missingCount = 0;
        totalCount = 0;
      }
      
      setMissingControlsCount(missingCount);
      setTotalControlsCount(totalCount);
      
      // Only show warning if there are missing controls
      if (missingCount > 0 && false) {
        setIsWarningModalOpen(true);
      } else {
        // If all controls have documents, proceed directly
        proceedWithAssessment();
      }
    } else {
      // For other assessment types, proceed directly
    
      setLoading(true);
      proceedWithAssessment();
    }
  };
  
  // Function to proceed with assessment after confirmation from warning modal
  const proceedWithAssessment = () => {

    setIsWarningModalOpen(false); // Close the modal
    setLoading(true);
    handleGenerateResponse(id, navigate)
      .then(() => {
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };
  
  // Function to close warning modal without proceeding
  const handleWarningModalClose = () => {
    setIsWarningModalOpen(false);
  };

  // Function to handle going back to review controls
  const handleBackToReviewResponse = () => {
    if (agentData?.stepData) {
      const updatedStepData = [...agentData.stepData];

      if(agentType === AGENT_TYPES.GAP_ASSESSMENT){

        for (let i = 0; i < updatedStepData.length; i++) {
        if (updatedStepData[i].type === AgentSessionStepType.CONTROL_SELECTION) {
          updatedStepData[i].status = AgentSessionStatus.COMPLETE;
        }
        if (updatedStepData[i].type === AgentSessionStepType.EDIT_RESPONSE) {
          updatedStepData[i].status = AgentSessionStatus.INPUT_NEEDED;
        }
      }

      }else{
      
      // Set evidence mapping to COMPLETE and EDIT_RESPONSE to IN_PROGRESS
      for (let i = 0; i < updatedStepData.length; i++) {
        if (updatedStepData[i].type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING) {
          updatedStepData[i].status = AgentSessionStatus.COMPLETE;
        }
        if (updatedStepData[i].type === AgentSessionStepType.EDIT_RESPONSE) {
          updatedStepData[i].status = AgentSessionStatus.IN_PROGRESS;
        }
        if (updatedStepData[i].type === AgentSessionStepType.GENERATE_RISK_ASSESSMENT_RESPONSE) {
          updatedStepData[i].status = AgentSessionStatus.COMPLETE;
        }
      }
      }
      
      // Update state
      setAgentData(id, {
        ...agentData,
        stepData: updatedStepData
      });
      
      // Exit reassessment mode
      setReassessmentMode(false, null);
      
      // Navigate back to agent page
      navigate(`/agent/${id}`);
    }
  };

  const inProgressOrInputNeeded = useMemo(() => {
    const inProgressSteps = [
      AgentSessionStatus.INPUT_NEEDED,
      AgentSessionStatus.IN_PROGRESS,
    ];
    return stepData?.find((step) => inProgressSteps.includes(step.status));
  }, [stepData]);

  const handleDownloadReport = useCallback(async (fileFormat?:string) => {
    setDownloadReportLoading(true);
    // wait for the saving process to finish if it's in progress
    // This is a workaround to ensure that the report is downloaded only after the saving process is complete.
    if (isSaving) {
      await new Promise((resolve) => {
        const checkSavingStatus = () => {
          if (!isSaving) {
            resolve(true);
          } else {
            // Check again after 50ms
            setTimeout(checkSavingStatus, 600);
          }
        };
        
        // Start checking
        checkSavingStatus();
        
        // Add a timeout to avoid infinite waiting
        setTimeout(() => resolve(false), 5000); // 5 second max wait
      });
    }

    await parsePromise(generateAndDownloadSessionReport(id, fileFormat));

    setDownloadReportLoading(false);
    setIsEndSessionModalOpen(false);
  }, [id, agentType, agentSubType, isSaving]);

  const processStage = useMemo(() => {
    // Check if this is a create_new_control_set session for multi-vendor assessment
    const isCreateNewControlSet = agentData?.sessionData &&
      "multi_file_vendor_assessment" in agentData.sessionData &&
      agentData.sessionData.multi_file_vendor_assessment &&
      agentData.sessionData.multi_file_vendor_assessment.create_new_control_set === true;

    // For create_new_control_set, we always want to show the control selection step
    if (isCreateNewControlSet && inProgressOrInputNeeded?.type === AgentSessionStepType.CONTROL_SELECTION) {
      const controlSelectionStep = stepData?.find(
        (step) => step.type === AgentSessionStepType.CONTROL_SELECTION
      );
      if (controlSelectionStep) {
        return AgentSessionStepType.CONTROL_SELECTION;
      }
    }
    
    // Standard logic for other cases
    if (
      inProgressOrInputNeeded &&
      [
        AgentSessionStepType.GENERATE_RISK_ASSESSMENT_RESPONSE,
        AgentSessionStepType.SOC2_TYPE2_AUDIT,
        // Removed MULTI_FILE_VENDOR_ASSESSMENT from this list so it doesn't get filtered out
        AgentSessionStepType.DORA_ASSESSMENT,
        AgentSessionStepType.MULTI_VENDOR_PROFILE,
      ].includes(inProgressOrInputNeeded.type)
    ) {
      return null;
    }

    if (inProgressOrInputNeeded?.type === AgentSessionStepType.EDIT_RESPONSE) {
      return AgentSessionStepType.EDIT_RESPONSE;
    }

    const prepareReportStep = stepData?.find(
      (step) => step.type === AgentSessionStepType.PREPARE_REPORT
    );
    if (
      prepareReportStep?.status === AgentSessionStatus.PENDING ||
      prepareReportStep?.status === AgentSessionStatus.COMPLETE
    ) {
      return AgentSessionStepType.PREPARE_REPORT;
    }

    return null;
  }, [stepData, inProgressOrInputNeeded, agentData]);

  const onGenerateResponse = async () => {
    try {
      setDownloadReportLoading(true);
      await generateResponseData({
        agentId: id,
      });
    } catch (error) {
      console.error(error)
      addNotification({
        type: "error",
        title: "Generate failed",
        message:
          (error as Error)?.message ??
          "Report generation failed. Please try again later",
      });
    } finally {
      setDownloadReportLoading(false);
    }
  };

  // Check if we're in the evidence mapping stage for NIST CSF 2.0
  const isNistCsfEvidenceMapping = useMemo(() => 
    inProgressOrInputNeeded?.type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING && 
    agentType === AGENT_TYPES.RISK_ASSESSMENT && 
    agentSubType === RiskAssessmentTypes.NIST_CSF_2
  , [inProgressOrInputNeeded?.type, agentType, agentSubType]);

  // Function to proceed with reassessment after confirmation from warning modal
  const proceedWithReassessment = async () => {
    setLoading(true);
    
    // For NIST CSF 2.0, we need to move any temporary uploaded files to permanent state
    if (agentType === AGENT_TYPES.RISK_ASSESSMENT && agentSubType === RiskAssessmentTypes.NIST_CSF_2 || agentType === AGENT_TYPES.GAP_ASSESSMENT) {
      try {

        // Get the customizeControls actions to update state
        const { setDataState, setChangesMade, setIsSavingControls } = getCustomizeControlsActions();
        
        // Flag to track if any files were moved
        let filesMoved = false;
        
        // Update each control's state - move temporary files to permanent state
        await new Promise<void>(resolve => {
          setDataState((prevControls) => {
            const updatedControls = prevControls.map(control => {
              // Only process NIST CSF controls in reassessment mode
              if (control.agentType === AgentSessionType.RISK_ASSESSMENT || agentType === AGENT_TYPES.GAP_ASSESSMENT) {
                const nistControl = control as NistCsfControl;
                
                // If control has temporary files, move them to permanent state
                if (nistControl.new_uploaded_files && nistControl.new_uploaded_files.length > 0) {
                  filesMoved = true;
                  
                  // Combine permanent and temporary files
                  const combinedUrls = [
                    ...(nistControl.source_urls || []),
                    ...(nistControl.new_uploaded_files || [])
                  ];
                  
                  const combinedFileNames = [
                    ...(nistControl.file_names || []),
                    ...(nistControl.new_uploaded_file_names || [])
                  ];
                  
                  // Return updated control with combined files and reset temporary files
                  return {
                    ...nistControl,
                    source_urls: combinedUrls,
                    file_names: combinedFileNames,
                    new_uploaded_files: [],
                    new_uploaded_file_names: []
                  };
                }
              }
              
              // Return unchanged control if it's not NIST CSF or has no temporary files
              return control;
            });
            
            setTimeout(resolve, 0); // Resolve after state update is queued
            return updatedControls;
          });
        });
        
        // Explicitly trigger autosave and increment the changesMade counter
        await new Promise<void>(resolve => {
          setIsSavingControls("EDITED");
          setChangesMade((prev) => {
            setTimeout(resolve, 0); // Resolve after state update is queued
            return prev + 1;
          });
        });
        
        // Explicitly call handleAutoSaveCustomizeControls to ensure file updates are saved
        await handleAutoSaveCustomizeControls(id, false);
        
        // Add a delay to ensure all states have been processed
        await new Promise(resolve => setTimeout(resolve, 500));
        
        
          // Update step status - set NIST_CSF_EVIDENCE_MAPPING to COMPLETE and GENERATE_RISK_ASSESSMENT_RESPONSE to IN_PROGRESS
          if (agentData?.stepData) {
            const updatedStepData = [...agentData.stepData];

            if(agentType === AGENT_TYPES.RISK_ASSESSMENT){

              for (let i = 0; i < updatedStepData.length; i++) {
                if (updatedStepData[i].type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING) {
                  updatedStepData[i].status = AgentSessionStatus.COMPLETE;
                }
              if (updatedStepData[i].type === AgentSessionStepType.GENERATE_RISK_ASSESSMENT_RESPONSE) {
                updatedStepData[i].status = AgentSessionStatus.UNDER_REASSESSMENT;
              }
            }
          } else if (agentType === AGENT_TYPES.GAP_ASSESSMENT) {
            for (let i = 0; i < updatedStepData.length; i++) {
              if (updatedStepData[i].type === AgentSessionStepType.CONTROL_SELECTION) {
                updatedStepData[i].status = AgentSessionStatus.COMPLETE;
              }
              if (updatedStepData[i].type === AgentSessionStepType.SOC2_TYPE2 || updatedStepData[i].type === AgentSessionStepType.SOC2_TYPE1) {
                updatedStepData[i].status = AgentSessionStatus.UNDER_REASSESSMENT;
              }
            }
          }
            // Update agent data with new step statuses
            setAgentData(id, {
              ...agentData,
              stepData: updatedStepData
            });
          }
      
        
        // Prepare inputs for processDataBasedOnAssessment
        const inputs = {
          title: agentData?.sessionData?.name || "",
          evidence_mapping: "customize",
          isReassessmentMode: true,
        };
        
        // Now use processDataBasedOnAssessment instead of handleGenerateResponse
        await processDataBasedOnAssessment(
          agentType,
          [], // Empty files array since we're using existing files
          navigate,
          inputs,
          agentSubType,
          false,
          selectedOptionRef.current,
          agentData,
          [] // Empty previous files since we've already combined them in the controls
        );
      } catch (err) {
        console.error("Error during reassessment:", err);
        addNotification({
          type: "error",
          title: "Error during reassessment",
          message: "There was a problem generating the response. Please try again."
        });
      } finally {
        setLoading(false);
      }
    } 
  };
  
  // Hoist warningType logic from the old WithWarningModal
  const warningTypeForModal = useMemo(() => {
    if ((isNistCsfEvidenceMapping || ifSessionHasEvidenceMapping) && isReassessmentMode && reassessmentAgentId === id) {
      return 'reassessment';
    }
    return 'assessment';
  }, [isNistCsfEvidenceMapping, isReassessmentMode, reassessmentAgentId, id]);

  // Determine the main content to render
  let mainContent: React.ReactNode = null;

  if (processStage) {
    if (
      inProgressOrInputNeeded?.type ===
      AgentSessionStepType.MANUAL_EXCEL_STRUCTURE_MAPPING
    ) {
      mainContent = (
        <QAGenerateButton
          onGenerateResponse={onGenerateResponse}
          loading={downloadReportLoading}
        />
      );
    } else if (
      inProgressOrInputNeeded?.type === AgentSessionStepType.CONTROL_SELECTION ||
      inProgressOrInputNeeded?.type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING
    ) {
      const anyControlInvalid =
        dataState.filter((control) => {
          if (
            agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP_TYPE1 ||
            agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP ||
            agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP_TYPE2
          ) {
            return (
              (control as Soc2Control).tsc_id?.replace(
                TSCABBRIVATIONS[(control as Soc2Control).tsc],
                ""
              ).length > 0 &&
              isInValidIdForSoc2(
                (control as Soc2Control).tsc_id.replace(
                  TSCABBRIVATIONS[(control as Soc2Control).tsc],
                  ""
                )
              )
            );
          }
          return false;
        }).length > 0;

      const isDuplicateId = (() => {
        // For multi-vendor assessment, skip duplicate validation
        if (agentTypeFromCustomizeControls === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
          return false;
        }
        
        // For other agent types, keep existing duplicate validation
        return new Set(
          dataState.map((c) => {
            if (
              agentTypeFromCustomizeControls ===
                AgentSessionType.SOC2_GAP_TYPE1 ||
              agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP ||
              agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP_TYPE2
            ) {
              return (c as Soc2Control).tsc_id;
            }

            return "";
          })
        ).size !== dataState.length;
      })();

      let startAssessmentWarningTooltip = "Please fix the errors (marked in red) to continue"

        const isDisabled = (() => {
          // Disable if no controls exist (for all agent types)
          if (!dataState || dataState.length === 0) {
            return true;
          }
          
          // For NIST CSF (Risk Assessment), only apply NIST-specific validation
          if (agentTypeFromCustomizeControls === AgentSessionType.RISK_ASSESSMENT) {
            return (
              isSavingControls === "SAVING" ||
              isSavingControls === "EDITED" ||
              (totalNistControls > 0 && nistControlsMissingFiles === totalNistControls)
            );
          }

          // For Gap Custom Framework
          if (agentTypeFromCustomizeControls === AgentSessionType.GAP_CUSTOM_FRAMEWORK) {
            return !dataState.filter((d: any) => {
              return !!(d.control_id && d.control);
            }).length || (
              isSavingControls === "SAVING" ||
              isSavingControls === "EDITED" ||
              (controlsCount > 0 && controlsCount === controlsWithMissingFiles)
            );
          }
          
          if(controlsCount > 0 && controlsCount === controlsWithMissingFiles){
            startAssessmentWarningTooltip = "Please upload the required documents for the controls to proceed"
          }
          
          // For other agent types, use existing validation logic
          return (
            !!dataState.filter((d) => {
              if (
                agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP_TYPE1 ||
                agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP ||
                agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP_TYPE2
              ) {
                return !isSoc2ControlValid(d as Soc2Control);
              }
              if (
                agentTypeFromCustomizeControls ===
                AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT
              ) {
                return !isVendorAssessmentControlValid(d as VendorAssessmentControl);
              }
              return true;
            }).length ||
            isSavingControls === "SAVING" ||
            isSavingControls === "EDITED" ||
            anyControlInvalid ||
            isDuplicateId || (controlsCount > 0 && controlsCount === controlsWithMissingFiles)
          );
        })();
      
      // Check if this is a create_new_control_set session
      const isCreateNewControlSet = agentData?.sessionData &&
        "multi_file_vendor_assessment" in agentData.sessionData &&
        agentData.sessionData.multi_file_vendor_assessment &&
        agentData.sessionData.multi_file_vendor_assessment.create_new_control_set === true;
      
      // Set button text based on mode
      const startAssessmentButtonText = isCreateNewControlSet ? "Continue" : "Start Assessment";
      
      // Handle continuing to vendor assessment intake form
      const handleContinue = () => {
        // Check if both vendor_filled_intake_form and create_new_control_set are true
        const isVendorFilledIntakeForm = agentData?.sessionData && 
          'multi_file_vendor_assessment' in agentData.sessionData && 
          agentData.sessionData.multi_file_vendor_assessment?.vendor_filled_intake_form;
        const isCreateNewControlSet = agentData?.sessionData && 
          'multi_file_vendor_assessment' in agentData.sessionData && 
          agentData.sessionData.multi_file_vendor_assessment?.create_new_control_set;
        
        if (isCreateNewControlSet) {
          // For this combination, send email and show AgentWaiting
          setLoading(true);
          handleGenerateResponse(id, navigate)
            .then(() => {
              setLoading(false);
            })
            .catch((err) => {
              console.error(err);
              setLoading(false);
            });
          return;
        }
        
        if (agentData?.stepData) {
          setLoading(true);
          // First, save the current controls
          handleAutoSaveCustomizeControls(id, true).then(() => {
            try {
              // Update step statuses
              const updatedStepData = [...agentData.stepData];
              
              // Find the vendor profile and control selection steps and update their statuses
              let vendorProfileStep = null;
              
              for (let i = 0; i < updatedStepData.length; i++) {
                // Mark control_selection step as COMPLETE
                if (updatedStepData[i].type === AgentSessionStepType.CONTROL_SELECTION) {
                  updatedStepData[i].status = AgentSessionStatus.COMPLETE;
                }
                
                // Find mfva_vendor_profile step and set as INPUT_NEEDED
                // if (updatedStepData[i].type === "mfva_vendor_profile") {
                //   updatedStepData[i].status = AgentSessionStatus.INPUT_NEEDED;
                //   vendorProfileStep = updatedStepData[i];
                // }
              }
              
              // Update agent data with new step statuses
              setAgentData(id, {
                ...agentData,
                stepData: updatedStepData
              });
              
              // Notify the user of successful progress
              addNotification({
                type: "success",
                title: "Controls Saved",
                message: "Your controls have been saved. Proceeding to vendor profile."
              });
              
              setLoading(false);
              
              // Navigate to the main agent page, which will show the vendor profile form
              // Force page reload to ensure the correct step is shown
              navigate(`/agent/${id}`);
            } catch (error) {
              console.error("Error updating step statuses:", error);
              throw error;
            }
          }).catch(err => {
            setLoading(false);
            console.error("Error saving controls:", err);
            addNotification({
              type: "error",
              title: "Error",
              message: "Failed to save controls. Please try again."
            });
          });
        }
      };

      mainContent = (
        <div className="flex items-center gap-4">
          {/* Show Back to Review Response button if in reassessment mode */}
          {(ifSessionHasEvidenceMapping || isNistCsfEvidenceMapping) && isReassessmentMode && reassessmentAgentId === id && (
            <ZButton 
              onClick={handleBackToReviewResponse}
              className="px-3 py-1 h-auto bg-white font-medium mt-0.5"
              color="primary"
              variant="bordered"
            >
              Back to Review Response
            </ZButton>
          )}

          {/* Show Start Reassessment button if in reassessment mode */}
          {(ifSessionHasEvidenceMapping || isNistCsfEvidenceMapping) && isReassessmentMode && reassessmentAgentId === id && (
            <ZButton 
              className="px-3 py-1 h-auto bg-primary text-white mt-0.5"
              onClick={() => {
                // For NIST CSF 2.0, check if documents are missing and show warning modal
                
                // Use pre-calculated values
                setMissingControlsCount(nistControlsMissingFiles || controlsWithMissingFiles);
                setTotalControlsCount(totalNistControls || controlsCount);
                
                // Only show warning if there are missing controls
                if (nistControlsMissingFiles > 0 || controlsWithMissingFiles) {
                  setIsWarningModalOpen(true);
                } else {
                  // If all controls have documents, proceed directly with reassessment
                  getPreviousFiles();
                  // Instead of setOpenReassess(true), directly show NistCsfReassessModal
                  // For non-NIST CSF 2.0 reassessments, or if it's not evidence mapping stage, open the generic modal
                  if ((agentType === AGENT_TYPES.RISK_ASSESSMENT && agentSubType === RiskAssessmentTypes.NIST_CSF_2) || agentType === AGENT_TYPES.GAP_ASSESSMENT ) {
                    setIsNistCsfReassessModalOpen(true);
                  } else {
                    setOpenReassess(true); // Fallback for other reassessment types
                  }
                }
              }}
              isDisabled={isDisabled}
            >
              Start Reassessment
            </ZButton>
          )}
          
          {/* Show Start Assessment button if NOT in reassessment mode */}
          {(!(isNistCsfEvidenceMapping || ifSessionHasEvidenceMapping) || !(isReassessmentMode && reassessmentAgentId === id)) && (
            <ZButton
              onClick={isCreateNewControlSet ? handleContinue : handleStartAssessmentClick}
              isLoading={loading}
              className={`px-3 py-1 h-auto mt-0.5 ${isDisabled ? 'cursor-not-allowed' : ''}`}
              isDisabled={isDisabled}
            >
              {isDisabled ? (
                <ZTooltip
                  content={startAssessmentWarningTooltip}
                  placement="left"
                  showArrow={true}
                  isDisabled={false} // Make ZTooltip active
                >
                  <span style={{ pointerEvents: "auto" }}>
                    {startAssessmentButtonText}
                  </span>
                </ZTooltip>
              ) : (
                startAssessmentButtonText
              )}
            </ZButton>
          )}
        </div>
      );
    } else if (
      processStage === AgentSessionStepType.EDIT_RESPONSE ||
      processStage === AgentSessionStepType.PREPARE_REPORT
    ) {
      const prepareReportStep = stepData?.find(
        (step) => step.type === AgentSessionStepType.PREPARE_REPORT
      );
      const FairDownloadOptions = [
        {
          key: 'pdf',
          label: 'Download as PDF File',
          onClick: () => handleDownloadReport('pdf'),
        },
        {
          key: 'docx',
          label: 'Download as Word File',
          onClick: () => handleDownloadReport('docx'),
        },
      ];

      const VendorAssessmentDownloadOptions = [
        {
          key: 'summary',
          label: 'Download Summary',
          onClick: () => handleDownloadReport('summary_pdf'),
        },
        {
          key: 'full-report',
          label: 'Download Full Report',
          onClick: () => handleDownloadReport(),
        },
      ];

      mainContent = (
        <div>
          <div className="flex items-center">
            <div className="p-1 py-[1px] bg-[#fafafa] px-2 rounded-lg mr-3 cursor-default">
              {agentType === AGENT_TYPES.RISK_ASSESSMENT && agentSubType !== RiskAssessmentTypes.FAIR ||
              agentType === AGENT_TYPES.GAP_ASSESSMENT || agentType === AGENT_TYPES.QUESTIONNAIRE  ? (
                <p className="text-[10px] text-[#52525B] leading-4 font-medium">{`${
                  approved?.length ?? 0
                } / ${ids?.length ?? 0} ${helperData.rowName}s accepted`}</p>
              ) : null}
            </div>
            
            {agentType === AGENT_TYPES.RISK_ASSESSMENT && agentSubType === RiskAssessmentTypes.FAIR ? (
              <div className="inline-flex items-center">
                <div className="inline-flex overflow-hidden rounded-md mr-2">
                  <ZDropdownButton
                    label="Download Report"
                    options={FairDownloadOptions}
                    isDisabled={downloadReportLoading}
                  />
                </div>
                {downloadReportLoading && (
                  <LoadingSpinner className="text-primary" containerClassName="text-primary" />
                )}
              </div>
            ) : agentType === AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT ? (
              <div className="flex items-center gap-2">
                <Dropdown
                  placement="bottom-end"
                  isDisabled={downloadReportLoading}
                  isOpen={isDownloadDropdownOpen}
                  onOpenChange={setIsDownloadDropdownOpen}
                >
                  <DropdownTrigger>
                    <div className="flex items-center h-7 border border-primary rounded-md overflow-hidden bg-white">
                      <button
                        disabled={downloadReportLoading}
                        className="px-3 py-1 text-sm font-medium text-primary outline-none focus:outline-none hover:bg-primary/5"
                        onClick={() => setIsDownloadDropdownOpen(true)}
                      >
                        Download
                      </button>
                      <button
                        disabled={downloadReportLoading}
                        className="flex items-center justify-center w-7 h-7 border-l border-primary outline-none focus:outline-none hover:bg-primary/5"
                        onClick={() => setIsDownloadDropdownOpen(true)}
                      >
                        <ChevronDown className="w-4 h-4 text-primary" />
                      </button>
                    </div>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="Download options"
                    onAction={(key) => {
                      const option = VendorAssessmentDownloadOptions.find((opt) => opt.key === key);
                      if (option) option.onClick();
                    }}
                  >
                    {VendorAssessmentDownloadOptions.map((option) => (
                      <DropdownItem key={option.key}>{option.label}</DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
                {downloadReportLoading && (
                  <LoadingSpinner className="text-primary" containerClassName="text-primary" />
                )}
                {showReassessControl && (
                  <ZButton
                    radius="none"
                    onClick={handleReassess}
                    isDisabled={prepareReportStep?.status === AgentSessionStatus.COMPLETE}
                    className="bg-primary text-white px-3 py-1 h-7 min-h-7 font-medium text-sm rounded-md"
                  >
                    Start Reassessment
                  </ZButton>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <ZButton
                  onClick={() => void handleDownloadReport()}
                  isLoading={downloadReportLoading}
                  isDisabled={downloadReportLoading}
                  className="px-3 py-1 h-auto"
                >
                 {agentType === AGENT_TYPES.POLICY_MANAGEMENT ? "Download Result"  : "Download Report"}
                </ZButton>
              </div>
            )
            }
            
            <div className="-ml-1">
              <Dropdown>
                <DropdownTrigger>
                  <Button isIconOnly className="bg-transparent">
                    <Ellipsis className="h-5 w-5" color="#717179" />
                  </Button>
                </DropdownTrigger>
                <DropdownMenu>
                  {(agentType === AGENT_TYPES.RISK_ASSESSMENT && agentSubType !== RiskAssessmentTypes.FAIR ||
                      (agentType === AGENT_TYPES.GAP_ASSESSMENT && ifSessionHasEvidenceMapping && agentSubType !== (GapAssessmentTypes.GAP_CUSTOM_FRAMEWORK as any))) && (
                      <DropdownItem
                        key="reassess"
                        onClick={handleReassess}
                        startContent={
                        <RotateCcw className="h-4 w-4 text-[#444444]" />
                      }
                      isDisabled={
                        prepareReportStep?.status === AgentSessionStatus.COMPLETE
                      }
                      className={`text-[#444444] ${
                        showReassessControl ? "" : "hidden"
                      }`}
                    >
                     Reassess Controls
                    </DropdownItem>
                    )}

                    {canAccessZaniaAskAi ? (
                      <DropdownItem
                        key="ask-ai"
                        onClick={() => setIsFloatingAskAiOpen(true)}
                        startContent={
                          <Sparkles className="h-4 w-4 text-[#444444]" />
                        }
                        className={`text-[#444444]`}
                      >
                        <div className="flex items-center gap-2">
                          <span>Ask Zania</span>
                          <div className="flex justify-center items-center px-2 gap-1 w-[42px] h-5 bg-[#FAFAFA] border-[0.5px] border-[#E4E4E7] rounded-[4px]">
                            <span
                              className="w-[26px] h-5 font-medium text-[10px] leading-5 uppercase"
                              style={{
                                fontFamily: 'Inter',
                                background: 'linear-gradient(180deg, #FF6496 0%, #812DD6 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                              }}
                            >
                              BETA
                            </span>
                          </div>
                        </div>
                      </DropdownItem>
                  ) : null as any}
                    <DropdownItem
                      key="share-session"
                      onClick={() => setIsShareSessionModalOpen(true)}
                      startContent={<Users className="h-4 w-4 text-[#444444]" />}
                      className="text-[#444444]"
                    >
                      Manage Collaborators
                    </DropdownItem>
                    <DropdownItem
                      key="share-session"
                      onClick={openViewFilesUsedDrawer}
                      startContent={<FileStack className="h-4 w-4 text-[#444444]" />}
                      className="text-[#444444]"
                    >
                      View Uploaded Files
                    </DropdownItem>
                    <DropdownItem
                      key="back-to-agent-home"
                      onClick={() => {
                        navigate(`/${backNav.route}`);
                      }}
                      startContent={
                        <ArrowLeft className="h-4 w-4 text-[#444444]" />
                      }
                      className="text-[#444444]"
                    >
                      {backNav.label}
                    </DropdownItem>

                  {/* Separator Line - Figma CSS */}
                  <DropdownItem
                    key="separator"
                    isReadOnly
                    className="h-0 border-t border-[#E4E4E7] p-0 cursor-default hover:bg-transparent"
                    textValue="separator"
                  >
                    <div className="h-0 w-full" />
                  </DropdownItem>

                  {agentType === AGENT_TYPES.RISK_ASSESSMENT && agentSubType === RiskAssessmentTypes.FAIR ? (
                    // FAIR-specific menu items
                    prepareReportStep?.status === AgentSessionStatus.COMPLETE ? (
                      <>
                        {/* Completed Label (Disabled) */}
                        <DropdownItem
                          key="completed"
                          startContent={
                            <CheckCheck className="h-4 w-4 text-[#444444]" />
                          }
                          isDisabled={true}
                          className="text-[#444444]"
                        >
                          Completed
                        </DropdownItem>

                        {/* Reopen Session Button */}
                        <DropdownItem
                          key="reopen-session"
                          onClick={() => {
                            setIsReopenSessionModalOpen(true);
                          }}
                          startContent={
                            <RotateCcw className="h-4 w-4 text-[#444444]" />
                          }
                          className="text-[#444444]"
                        >
                          Reopen Assessment
                        </DropdownItem>
                      </>
                    ) : (
                      <DropdownItem
                        key="mark-complete"
                        onClick={() => {
                          setIsEndSessionModalOpen(true);
                        }}
                        startContent={
                          <CheckCheck className="h-4 w-4 text-[#444444]" />
                        }
                        className="text-[#444444]"
                      >
                        Mark as Complete
                      </DropdownItem>
                    )
                  ) : (
                    // For all other agents: Keep existing "End Session" behavior
                    <DropdownItem
                      key="end-session"
                      onClick={() => {
                        setIsEndSessionModalOpen(true);
                      }}
                      startContent={
                        <CheckCircleIcon className="h-4 w-4 text-[#444444]" />
                      }
                      isDisabled={
                        prepareReportStep?.status === AgentSessionStatus.COMPLETE
                      }
                      className="text-[#444444]"
                    >
                      Mark as Complete
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </Dropdown>
            </div>
            <div className="modal-container">
              <ReassessModal
                agentData={agentData}
                isOpen={openReassess}
                onClose={onClose}
                uniqueSources={uniqueFiles}
              />
            </div>
          </div>
          <EndSessionModal
            isOpen={isEndSessionModalOpen}
            onClose={() => setIsEndSessionModalOpen(false)}
            onDownload={() => void handleEndSessionWithLoading()}
            responses={{
              approved: approved.length,
              total: ids?.length ?? 0,
            }}
            loading={endSessionLoading}
            agentSubType={agentSubType}
          />
          {agentType === AGENT_TYPES.RISK_ASSESSMENT && agentSubType === RiskAssessmentTypes.FAIR && (
            <ReopenSessionModal
              isOpen={isReopenSessionModalOpen}
              onClose={() => setIsReopenSessionModalOpen(false)}
              onConfirm={handleReopenSession}
              loading={endSessionLoading}
            />
          )}
          {isShareSessionModalOpen && (
            <ShareSessionModal
              onClose={() => setIsShareSessionModalOpen(false)}
            />
          )}
        </div>
      );
    }
  }
  // MULTI_VENDOR_PROFILE Continue button is now in each step component's bottom bar
  
  // For create_new_control_set sessions, always render a continue button even if processStage is null
  if (mainContent === null) {
    const isCreateNewControlSet = agentData?.sessionData &&
      "multi_file_vendor_assessment" in agentData.sessionData &&
      agentData.sessionData.multi_file_vendor_assessment &&
      agentData.sessionData.multi_file_vendor_assessment.create_new_control_set === true;
      
    if (isCreateNewControlSet) {
      // Find the CONTROL_SELECTION step to determine if we're in that stage
      const controlSelectionStep = stepData?.find(
        (step) => step.type === AgentSessionStepType.CONTROL_SELECTION && 
                 (step.status === AgentSessionStatus.INPUT_NEEDED || step.status === AgentSessionStatus.IN_PROGRESS)
      );
      
      if (controlSelectionStep) {
        const handleContinue = async () => {
          // Check if both vendor_filled_intake_form and create_new_control_set are true
          const isVendorFilledIntakeForm = agentData?.sessionData && 
            'multi_file_vendor_assessment' in agentData.sessionData && 
            agentData.sessionData.multi_file_vendor_assessment?.vendor_filled_intake_form;
          const isCreateNewControlSet = agentData?.sessionData && 
            'multi_file_vendor_assessment' in agentData.sessionData && 
            agentData.sessionData.multi_file_vendor_assessment?.create_new_control_set;
          
          if (isVendorFilledIntakeForm && isCreateNewControlSet) {
            // For this combination, send email and show AgentWaiting
            setLoading(true);
            handleGenerateResponse(id, navigate)
              .then(() => {
                setLoading(false);
              })
              .catch((err) => {
                console.error(err);
                setLoading(false);
              });
            return;
          }
          
          if (agentData?.stepData) {
            try {
              setLoading(true);
              // First, save the current controls
              await handleAutoSaveCustomizeControls(id, true);
              
              // Update step statuses
              const updatedStepData = [...agentData.stepData];
              
              // Find and update the CONTROL_SELECTION step to COMPLETE
              const controlSelectionStep = updatedStepData.find(
                step => step.type === AgentSessionStepType.CONTROL_SELECTION
              );
              
              if (controlSelectionStep) {
                controlSelectionStep.status = AgentSessionStatus.COMPLETE;
                
                // Update agent data with new step statuses
                setAgentData(id, {
                  ...agentData,
                  stepData: updatedStepData
                });

                // Call the step update API for the CONTROL_SELECTION step
                await updateAgentSessionStep(controlSelectionStep as any);
              }
              
              
              setLoading(false);
              
              // Navigate to the main agent page, which will show the vendor profile form
              navigate(`/agent/${id}`);
            } catch (error) {
              console.error("Error updating step statuses:", error);
              setLoading(false);
              console.error("Error saving controls:", error);
              addNotification({
                type: "error",
                title: "Error",
                message: "Failed to save controls. Please try again."
              });
            }
          }
        };
        
        // Create validation logic for the fallback Continue button
        const isContinueDisabled = (() => {
          // Apply the same validation logic as the Save as Default Controls
          if (!dataState || dataState.length === 0) {
            return true;
          }
          
          // Recalculate validation variables for this scope
          const anyControlInvalid =
            dataState.filter((control) => {
              if (
                agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP_TYPE1 ||
                agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP ||
                agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP_TYPE2
              ) {
                return (
                  (control as Soc2Control).tsc_id?.replace(
                    TSCABBRIVATIONS[(control as Soc2Control).tsc],
                    ""
                  ).length > 0 &&
                  isInValidIdForSoc2(
                    (control as Soc2Control).tsc_id.replace(
                      TSCABBRIVATIONS[(control as Soc2Control).tsc],
                      ""
                    )
                  )
                );
              }
              return false;
            }).length > 0;

          const isDuplicateId = (() => {
            // For multi-vendor assessment, skip duplicate validation
            if (agentTypeFromCustomizeControls === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
              return false;
            }
            
            // For other agent types, keep existing duplicate validation
            return new Set(
              dataState.map((c) => {
                if (
                  agentTypeFromCustomizeControls ===
                    AgentSessionType.SOC2_GAP_TYPE1 ||
                  agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP ||
                  agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP_TYPE2
                ) {
                  return (c as Soc2Control).tsc_id;
                }
                return "";
              })
            ).size !== dataState.length;
          })();
          
          return (
            !!dataState.filter((d) => {
              if (
                agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP_TYPE1 ||
                agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP ||
                agentTypeFromCustomizeControls === AgentSessionType.SOC2_GAP_TYPE2
              ) {
                return !isSoc2ControlValid(d as Soc2Control);
              }
              if (
                agentTypeFromCustomizeControls ===
                AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT
              ) {
                return !isVendorAssessmentControlValid(d as VendorAssessmentControl);
              }
              return false; 
            }).length ||
            isSavingControls === "SAVING" ||
            isSavingControls === "EDITED" ||
            anyControlInvalid ||
            isDuplicateId
          );
        })();
        
        // Create a fallback content if mainContent is null but we're in create_new_control_set mode
        mainContent = (
          <div className="flex items-center gap-4">
            <ZButton
              onClick={handleContinue}
              isLoading={loading}
              className="px-3 py-1 h-auto mt-0.5"
              isDisabled={isContinueDisabled || loading}
            >
              Continue
            </ZButton>
          </div>
        );
      }
    }
  }

  return (
    <>
      {mainContent}

      {/* Warning Modal for NIST CSF controls with missing documents */}
      <WarningModal
        isOpen={isWarningModalOpen}
        onClose={() => {
          handleWarningModalClose();
        }}
        onConfirm={() => {
          if (warningTypeForModal === 'reassessment') {
            // Instead of calling proceedWithReassessment directly, show the NistCsfReassessModal
            setIsWarningModalOpen(false);
            setIsNistCsfReassessModalOpen(true);
          } else {
            proceedWithAssessment();
          }
        }}
        title="Proceed without Documents for Some Controls?"
        message={`Documents missing for ${missingControlsCount} of ${totalControlsCount} controls.`}
      />
      
      {/* Add the new NistCsfReassessModal here */}
      <NistCsfReassessModal
        isOpen={isNistCsfReassessModalOpen}
        onClose={() => setIsNistCsfReassessModalOpen(false)}
        onProceed={(option) => {
          // Update both the ref (synchronous) and state (for any component that needs it)
          selectedOptionRef.current = option;
          setSelectedReassessOption(option);
          // Close the modal before proceeding with reassessment
          setIsNistCsfReassessModalOpen(true);
          // Then call proceedWithReassessment
          proceedWithReassessment(); 
        }}
        loading={loading}
      />

      <ViewSessionsFilesDrawer />
    </>
  );
};

export default AgentHeader;